import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { Api, SHIKHAR_HOME } from '@/lib/utils'
import { LoginApiResponse } from '@/lib/interfaces'
import { toast } from 'sonner'

export const Route = createFileRoute('/loginShikhar')({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>): { token: string } => ({
        token: typeof search.token === 'string' ? search.token : ''
    })
})

function RouteComponent() {
    const navigate = useNavigate()
    const { token } = Route.useSearch()
    const hasTriggered = useRef(false)

    const { setLogin } = useStore(useShallow(state => ({
        setLogin: state.setLogin
    })))

    const { mutate, isPending } = useMutation({
        mutationFn: (tokenValue: string) => {
            return Api<LoginApiResponse>('/webui/verifyShikharAccount', "POST", { token: tokenValue })
        },
        onSuccess: (data) => {
            setLogin(data)
            toast.success('Login successful')
            navigate({ to: '/shikhar/neworder' })
        },
        onError: (error) => {
            const message = error.message || 'Unable to verify Shikhar account'
            toast.error(message)
            if (message === 'Expired token!') {
                setTimeout(() => {
                    window.location.href = SHIKHAR_HOME
                }, 2000)
            }
        }
    })

    useEffect(() => {
        if (!token) {
            toast.error('Missing login token')
            navigate({ to: '/shikhar/neworder' })
            return
        }

        if (hasTriggered.current) {
            return
        }

        hasTriggered.current = true
        mutate(token)
    }, [token, mutate, navigate])

    return (
        <div className='fixed inset-0 flex flex-col items-center justify-center gap-2 bg-white text-center'>
            <p className='text-lg font-semibold'>Verifying Shikhar account…</p>
            <p className='text-sm text-gray-500'>
                {isPending ? 'Hang tight while we complete your login.' : 'Redirecting you to your dashboard.'}
            </p>
        </div>
    )
}
